import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { computeEffective, type EffectivePermissions } from '@dental-flow/rbac';
import { authApi, restoreSession, tenantApi } from '@/api/auth.api';
import { auth } from '@/api/client';
import { applyBranding } from '@/lib/branding';
import i18n from '@/i18n';
import { resolveLocale } from '@/i18n/resolve-locale';
import { clearVolumeCache } from '@/features/imaging/volume-cache';
import { readDemoView, writeDemoView, demoViewHides, type DemoView } from './demo-view';
import type { CurrentUser, PublicTenant } from '@/api/types';

/**
 * Who is signed in, and which clinic this is.
 *
 * Both are resolved once at startup and held here. The clinic comes from the
 * hostname the app was loaded from, so the same bundle renders as whichever
 * clinic the browser asked for.
 */
interface SessionValue {
  tenant: PublicTenant | null;
  user: CurrentUser | null;
  /** Effective permissions, in the same shape the API uses to enforce them. */
  permissions: EffectivePermissions;
  status: 'loading' | 'ready' | 'error';
  error: string | null;
  login: (email: string, password: string, mfaCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /** Re-read the clinic — its modules change from Setări → Personalizare → Module. */
  refreshTenant: () => Promise<void>;
  /** Does the signed-in user hold this permission? */
  can: (permission: string) => boolean;
  /**
   * Is this module switched on for this clinic?
   *
   * Distinct from `can`: a permission says what this person may do, a feature
   * says what the clinic has — its contract and its own choice on the modules
   * screen, combined by the server. A clinic with no laboratory should not see
   * a laboratory, however senior the user.
   */
  hasFeature: (feature: string) => boolean;
  /**
   * Vederea aleasă de vizitatorul demo — `null` peste tot în altă parte.
   *
   * Nu e o a treia sursă de adevăr lângă contract și `org_config.modules`: nu
   * ajunge niciodată la server și nu se aplică decât pe o clinică demo. Vezi
   * `demo-view.ts`.
   */
  /**
   * Practică clinica specialitatea asta?
   *
   * Separat de `hasFeature`: un modul e o parte a produsului, o specialitate e
   * un fapt despre cabinet. O clinică fără lista asta (server mai vechi, sau
   * una care n-a ales niciodată) le are pe toate — ascunderea e o alegere, și
   * una pe care nimeni n-a făcut-o nu are voie să ia nimic de pe ecran.
   */
  hasSpecialty: (key: string) => boolean;
  demoView: DemoView | null;
  setDemoView: (key: string | null) => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<PublicTenant | null>(null);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [status, setStatus] = useState<SessionValue['status']>('loading');
  const [error, setError] = useState<string | null>(null);
  // Citită o dată, din browser. Ține aici ca s-o vadă și pagina de login (unde
  // se alege), și bara de sus (unde se schimbă), și meniul — dintr-o singură
  // stare, deci fără reîncărcare între ele.
  const [demoView, setDemoViewState] = useState<DemoView | null>(() => readDemoView());

  const setDemoView = useCallback((key: string | null) => {
    writeDemoView(key);
    setDemoViewState(readDemoView());
  }, []);

  // The clinic, for the language resolver — read from callbacks that were
  // created before it loaded. `applyBranding` sets the clinic default; this
  // narrows it to the signed-in person's preference, if the clinic enabled it.
  const tenantRef = useRef<PublicTenant | null>(null);
  const applyUserLocale = useCallback((u: CurrentUser | null) => {
    const t = tenantRef.current;
    if (!t) return;
    void i18n.changeLanguage(
      resolveLocale({
        userPref: u?.preferredLanguage,
        clinicDefault: t.i18n?.default,
        enabled: t.i18n?.enabled,
        country: t.country,
      }),
    );
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // The clinic first: its branding is needed even on the login screen,
        // and a failure here means this hostname serves nothing.
        const t = await tenantApi.current();
        if (cancelled) return;
        setTenant(t);
        tenantRef.current = t;
        applyBranding(t);

        // Then the session, if a refresh token survived the reload.
        const restored = await restoreSession();
        if (cancelled) return;
        setUser(restored);
        applyUserLocale(restored);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : i18n.t('common:nuSAPutut'));
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applyUserLocale]);

  // The API client cannot import React state, so it calls back here when a
  // refresh fails and the session is gone for good.
  useEffect(() => {
    auth.onSessionLost(() => {
      setUser(null);
      applyUserLocale(null);
    });
  }, [applyUserLocale]);

  const login = useCallback(
    async (email: string, password: string, mfaCode?: string) => {
      const result = await authApi.login(email, password, mfaCode);
      setUser(result.user);
      applyUserLocale(result.user);
    },
    [applyUserLocale],
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    // Bucățile CBCT ținute minte sunt imagini ale pacienților: nu rămân în
    // browser după ce omul a ieșit din cont.
    await clearVolumeCache();
    setUser(null);
    // Back to the clinic's default language, off the signed-out person's.
    applyUserLocale(null);
  }, [applyUserLocale]);

  const refreshUser = useCallback(async () => {
    const me = await authApi.me();
    setUser(me);
    applyUserLocale(me);
  }, [applyUserLocale]);

  // After Setări → Personalizare → Module, so the menu, the routes and every
  // `hasFeature` read the clinic's new modules without a reload.
  const refreshTenant = useCallback(async () => {
    const t = await tenantApi.current();
    setTenant(t);
    tenantRef.current = t;
  }, []);

  /**
   * Permissions are recomputed with the same evaluator the API uses.
   *
   * The server sends the resolved list, so this is not re-deriving policy —
   * it is putting the list into the shape `has()` expects, including the
   * legacy-name translation.
   */
  const permissions = useMemo(
    () => computeEffective({ rolePermSets: user ? [user.permissions] : [] }),
    [user],
  );

  const can = useCallback((permission: string) => permissions.has(permission), [permissions]);

  const hasFeature = useCallback(
    // Unknown features default to on: a module that predates the flag, or one
    // a clinic's config has not been updated for, should keep working.
    //
    // Pe clinica demo se mai scade o dată ce a ales vizitatorul (`demo-view`).
    // Condiția `tenant.demo` nu e o precauție: e ce face alegerea inertă
    // oriunde altundeva, chiar dacă cheia a rămas în `localStorage` de pe
    // demo — o clinică adevărată nu-și pierde un modul pentru că cineva a
    // vizitat demo-ul de pe browserul ăsta.
    (feature: string) =>
      tenant?.features?.[feature] !== false &&
      !(tenant?.demo ? demoViewHides(demoView, feature) : false),
    [tenant, demoView],
  );

  const hasSpecialty = useCallback(
    (key: string) => !tenant?.specialties || tenant.specialties.includes(key),
    [tenant],
  );

  const value = useMemo<SessionValue>(
    () => ({
      tenant,
      user,
      permissions,
      status,
      error,
      login,
      logout,
      refreshUser,
      refreshTenant,
      can,
      hasFeature,
      hasSpecialty,
      demoView,
      setDemoView,
    }),
    [
      tenant,
      user,
      permissions,
      status,
      error,
      login,
      logout,
      refreshUser,
      refreshTenant,
      can,
      hasFeature,
      hasSpecialty,
      demoView,
      setDemoView,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}

/** Convenience for the common case of gating a control on one permission. */
export function useCan(): (permission: string) => boolean {
  return useSession().can;
}
